<template>
	<el-row>
		<el-col :span="24" style="text-align: left;">
			<div class="base-form header">
				<h2>Контрагенты</h2>
				<el-button type="primary" :icon="circleIcon" size="large" @click="onAddCounterpartyClick">
					Новый контрагент
				</el-button>
			</div>
		</el-col>
	</el-row>

	<el-row>
		<el-col :span="6">
			<div class="base-form filter">
				<el-input
					v-model="counterpartySearchFilter"					
					placeholder="Поиск контрагентов по инициалам, телефону, почте"
					clearable
					@clear="fetchCounterpartiesAsync"
					@keyup.enter="fetchCounterpartiesAsync"
					class="input-with-select">
					<template #append>
						<el-button :icon="searchIcon" />
					</template>
				</el-input>
			</div>
		</el-col>
	</el-row>

	<el-row>
		<el-col :span="6">
			<div class="base-form filter">
				<vehicle-filter-component
					@selected="onVehicleFilterChange" 
					placeholder="Поиск контрагентов по марке, модели, VIN автомобиля" />
			</div>
		</el-col>		
	</el-row>

	<el-row>
		<el-col :span="6">
			<div class="base-form filter">
				<el-select
					multiple
					v-model="counterpartyTypesFilter"
					placeholder="Фильтровать контрагентов по типу"
					@change="fetchCounterpartiesAsync">
					<template #label="scope">
						<counterparty-type-component :type="scope.value" />
					</template>
					<el-option
						v-for="item in availCounterpartyTypes"
						:key="item"
						:value="item">
						<counterparty-type-component :type="item" />
					</el-option>
				</el-select>
			</div>
		</el-col>
		<el-col :span="6">
			<div class="base-form filter">
				<el-select 				
					v-model="counterpartyManagersFilter" 
					placeholder="Фильтровать контрагентов по менеджерам" 
					@change="fetchCounterpartiesAsync"
					multiple>
					<el-option v-for="m in managers"
						:key="m.id"
						:value="m.id"
						:label="`${m.lastname} ${m.firstname.substring(0,1)}.`">
					</el-option>
				</el-select>		
			</div>
		</el-col>
	</el-row>	

	<el-row>
		<el-col :span="24">
			<el-table
				:data="tableData"
				v-loading="tableLoading"
				sortable="custom"				
				:default-sort="{ prop: tableSortColumn, order: tableSortOrder }"
				@sort-change="onTableSortingChange"
				@row-click="onTableRowClick">		
				<template #empty>
					<el-text type="info">Список контрагентов пуст ☹️</el-text>
				</template>
				<el-table-column v-if="isPickerMode" width="100">
					<template #default="scope">
						<el-button
							size="small"
							type="primary"
							@click="(e) => onTablePickCounterpartyClick(scope.row, e)">
							Выбрать
						</el-button>
					</template>
				</el-table-column>
				<el-table-column label="Дата регистрации" width="220" :formatter="(r) => dateFormatter(r.date)" sortable prop="date" />
				<el-table-column label="Инициалы" sortable prop="counterparty">
					<template #default="scope">
						{{ scope.row.lastname }}
						{{ scope.row.firstname }}
						{{ scope.row.middlename }}
						<div v-if="scope.row.legalEntity?.name">
							<el-text type="info" size="small">
								{{ scope.row.legalEntity.name }}
							</el-text>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="Тип контрагента">
					<template #default="scope">
						<div v-for="t in scope.row.types" :key="t" style="display: inline-block; margin-right: .3rem">
							<counterparty-type-component :type="t" />
						</div>						
					</template>
				</el-table-column>
				<el-table-column label="Телефон">
					<template #default="scope">
						{{ scope.row.phone }}
					</template>
				</el-table-column>
				<el-table-column label="Менеджер" sortable prop="manager">
					<template #default="scope">
						<text v-if="scope.row.manager?.id">
							{{ scope.row.manager.lastname }}
							{{ scope.row.manager.firstname ? `${scope.row.manager.firstname.substring(0,1)}.` : '' }}
						</text>
						<el-text v-else type="info" size="small">не назначен</el-text>
					</template>
				</el-table-column>
				<el-table-column label="Автомобиль" sortable prop="vehicle">
					<template #default="scope">
						<div 
							v-for="v in scope.row.vehicles" 
							:key="v.id" 
							style="display: inline-block; margin-right: .3rem">
							<el-tag size="small" :type="v.ownerChanged ? 'info' : 'primary'">
								{{ v?.model.manufacturer?.name }}&nbsp;{{ v?.model?.name }}
							</el-tag>
						</div>
					</template>
				</el-table-column>
				<el-table-column v-if="!isPickerMode" align="right" width="120">
					<template #default="scope">
						<el-button
							size="small"
							type="danger"
							@click="(e) => onTableDeleteVehicleClick(scope.row, e)">
							Удалить
						</el-button>
					</template>
				</el-table-column>
			</el-table>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import store from '@/store';
import { CirclePlus, Search } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElTag, ElLink, ElNotification, ElMessageBox } from 'element-plus';
import { date as dateFormatter } from '@/helpers/formatters';
import Vehicle from '@/models/Vehicle';
import TabsDictionary from '@/variables/tabs';
import axiosHelper from '@/helpers/axiosHelper';
import EventHelper from '@/helpers/eventsHelper';
import ApplicationTab from '@/models/ApplicationTab';
import OrderApiHelper from '@/helpers/api/OrderApiHelper';
import CounterpartyModel from '@/models/CounterpartyModel';
import PickerConfigurationModel from '@/models/PickerConfigurationModel';
import EmployeesApiHelper from '@/helpers/api/EmployeesApiHelper';
import EmployeeModel from '@/models/EmployeeModel';
import VehicleFilterComponent from '@/components/filters/VehicleFilterComponent.vue';
import ViewType from '@/models/enums/ViewType';
import CounterpartyTypeComponent from '@/components/CounterpartyTypeComponent.vue';
import CounterpartyType from '@/models/enums/CounterpartyType';

@Options({
	components: {
		CirclePlus, ElButton, ElTable, ElTag, ElLink,
		VehicleFilterComponent, CounterpartyTypeComponent
	},
	props: {
		params: Array
	}
})
export default class CounterpartiesView extends Vue {
	/** props */
	params!: Array<any>;

	/** constants */
	tableLoadingSvg = `<path class="path" d="M 30 15 L 28 17 M 25.61 25.61 A 15 15, 0, 0, 1, 15 30 A 15 15, 0, 1, 1, 27.99 7.5 L 15 15" style="stroke-width: 4px; fill: rgba(0, 0, 0, 0)"/>`;
	tableSortColumn = 'model';
	tableSortOrder = 'ascending';
	managers: Array<EmployeeModel> = [];
	availCounterpartyTypes = [ 
		CounterpartyType.Consumer, 
		CounterpartyType.Contractor, 
		CounterpartyType.Supplier
	];

	isPickerMode = false;
	onPickedCallback: Function | null = null;	
	queryCounterpartiesHttpClient = axiosHelper.createInstance(store.getters.jwt);
	reloadTableEvent = 'counterpartiesListUpdate';
	circleIcon = CirclePlus;
	searchIcon = Search;
	
	/** view-models */		
	tableLoading = false;
	tableFilterCounterpatyId: number | null = null;
	counterpartySearchFilter: string | null = null;
	counterpartyTypesFilter: Array<number> = [];
	counterpartyManagersFilter: Array<number> = [];
	counterpartyVehiclesFilter: Array<number> = [];
	tableData: Array<CounterpartyModel> = [];
	
	
	mounted() {
		this.initView();
		this.fetchManagers();

		EventHelper.setEventListener(this.reloadTableEvent, this.fetchCounterpartiesAsync);
		EventHelper.dispatchEvent(this.reloadTableEvent);
	}

	unmounted() {
		EventHelper.removeEventListener(this.reloadTableEvent, this.fetchCounterpartiesAsync);
	}

	initView() {
		const pickerModeArgument = this.params?.length ? this.params[0] as PickerConfigurationModel : null;
		/** check if picker mode activated */
		if (pickerModeArgument?.isPickerModeEnabled === true &&
			pickerModeArgument?.onItemPickedCallback) {
			this.isPickerMode = true;
			this.onPickedCallback = pickerModeArgument.onItemPickedCallback!;
		}
	}

	onAddCounterpartyClick() {
		const counterpartyViewTab = TabsDictionary.counterparty;
		counterpartyViewTab.params = [ null, ViewType.Creating ];
		counterpartyViewTab.allowMultiple = true;
		store.commit('addTab', counterpartyViewTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onTableRowClick(counterparty: CounterpartyModel) {
		const counterpartyViewTab = TabsDictionary.counterparty;
		counterpartyViewTab.params = [ counterparty.id, ViewType.Viewing ];
		counterpartyViewTab.allowMultiple = true;
		store.commit('addTab', counterpartyViewTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onTableSortingChange(eventObject: any) {
		this.tableSortColumn = eventObject.prop;
		this.tableSortOrder = eventObject.order;
		EventHelper.dispatchEvent(this.reloadTableEvent);
	}

	onCounterpartyFilterChanged(counterparty?: CounterpartyModel) {
		if (counterparty?.id) {
			this.tableFilterCounterpatyId = counterparty.id;
		} else {
			this.tableFilterCounterpatyId = null;
		}

		EventHelper.dispatchEvent(this.reloadTableEvent);
	}

	/** handles callback and passes selected counterparty to a parent component (if picker-mode is enabled) */	
	onTablePickCounterpartyClick(counterparty: CounterpartyModel, e: Event) {
		e.stopPropagation();
		if (this.onPickedCallback) {
			this.onPickedCallback(counterparty);
		}
	}

	onVehicleFilterChange(vehicle?: Vehicle) {
		if (vehicle?.id) {
			this.counterpartyVehiclesFilter = [vehicle.id];
		} else {
			this.counterpartyVehiclesFilter = [];
		}
		EventHelper.dispatchEvent(this.reloadTableEvent);
	}

	async fetchCounterpartiesAsync() {
		try {
			this.tableLoading = true;
			const filterObject = {
				types: this.counterpartyTypesFilter?.length ? this.counterpartyTypesFilter.join(',') : null,
				managers: this.counterpartyManagersFilter?.length ? this.counterpartyManagersFilter.join(',') : null,		
				vehicles: this.counterpartyVehiclesFilter?.length ? this.counterpartyVehiclesFilter.join(',') : null,						
				orderBy: this.tableSortColumn,
				orderAsc: this.tableSortOrder == 'ascending',
				query: this.counterpartySearchFilter
			};
			const response = await this.queryCounterpartiesHttpClient.get('/api/counterparties', { params: filterObject });
			if (!response.data?.length) {
				this.tableData = [];
				return;
			}
			this.tableData = response.data.map((_: any) => new CounterpartyModel(_));
		} catch (e: any) {
			if (e.code == 'ERR_NETWORK') {
				ElNotification({
					message: 'Ошибка соединения, не удалось загрузить список контрагентов',
					type: 'error',
				});
			} else {
				ElNotification({
					message: 'Ошибка загрузки списка контрагентов',
					type: 'error',
				});
			}
			console.error('Error loading counterparties list', e)
		} finally {
			this.tableLoading = false;
		}
	}

	async deleteCounterpartyAsync(counterparty: CounterpartyModel) {		
		try {
			this.tableLoading = true;
			const deleteCounterpartyHttpClient = axiosHelper.createInstance(store.getters.jwt);
			await deleteCounterpartyHttpClient.delete(`/api/counterparties/${counterparty.id}`);
			this.tableLoading = false;
			EventHelper.dispatchEvent(this.reloadTableEvent);
			ElNotification({				
				message: `Контрагент ${counterparty.lastname} ${counterparty.firstname} удален`,
				type: 'success',
			});
		} catch(e: any) {
			this.tableLoading = false;
			if (e?.status === 409) {
				ElNotification({				
					message: `Ошибка удаления контрагента ${counterparty.lastname} ${counterparty.firstname} - у контрагента есть заказ-наряды`,
					type: 'error',
				});
				return;
			}

			ElNotification({
				message: `Ошибка удаления контрагента ${counterparty.lastname} ${counterparty.firstname}`,
				type: 'error',
			});
			console.error(`Unable to delete counterparty #${counterparty.id}:`, e);
		}
	}

	fetchManagers() {
		new EmployeesApiHelper()
			.getManagersAsync(store.getters.jwt)
			.then((managers: Array<EmployeeModel>) => this.managers = managers);
	}
	
	onTableDeleteVehicleClick(counterparty: CounterpartyModel, e: Event) {
		e.stopPropagation();
			
		new OrderApiHelper(store.getters.jwt)
			.getOrdersByCounterpartyAsync(store.getters.jwt, counterparty.id)
			.then(orders => {
				if (orders!.length != 0) {
					ElMessageBox.confirm(
						'Удаление невозможно, у контрагента есть заказ-наряды', {
						confirmButtonText: 'OK',
						type: 'error',
					});
				} else {
					ElMessageBox.confirm(
						`Контрагент ${counterparty.lastname} ${counterparty.firstname} будет удален`, {
						confirmButtonText: 'OK',
						cancelButtonText: 'Отмена',
						type: 'warning',
					})
					.then(() => this.deleteCounterpartyAsync(counterparty));
				}
			});
	}

	dateFormatter(date: Date) {
		return dateFormatter.dateTime(date, true, true, false, ', ');
	}
}
</script>